'use client';

import { useEffect, useMemo, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Ban, Clock, Eye, FolderSearch, Gavel, HelpCircle, History,
  MessageSquareWarning, Search, X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  bandejaDecisiones, labelEstadoCorto, labelEstadoDecision, labelTipoDecision,
  type DecisionBandeja, type EstadoDecisionInicial, type TipoDecisionInicial,
} from '@/lib/decisionFlujo';
import { usuarioActual } from '@/lib/instruccion';
import { cn } from '@/lib/utils';
import StatCard from '@/components/StatCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

function fmtFecha(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-CO');
}

// Texto de ayuda según el rol: qué representa la bandeja para quien la abre.
function ayudaPorRol(rol: string): string {
  switch (rol) {
    case 'CONTRATISTA_INSTRUCCION':
    case 'TECNOLOGO_APOYO':
      return 'Casos por decidir (auto inhibitorio / indagación / investigación), armar el documento y proponer; y los devueltos a corregir.';
    case 'ABOGADO_ESPECIALIZADO':
      return 'Documentos por revisar: dé el visto bueno para pasarlos al Director o devuélvalos a corregir.';
    case 'DIRECTOR_INSTRUCCION':
      return 'Decisiones pendientes de su aprobación final (materializan la actuación).';
    case 'TECNICO_ADMINISTRATIVO':
      return 'Casos devueltos para corregir la pre-radicación (Etapa 1).';
    case 'SUPERVISOR':
    case 'APOYO_SUPERVISOR':
      return 'Seguimiento de las decisiones iniciales (solo lectura).';
    default:
      return 'Trámite de decisión inicial de cada expediente radicado.';
  }
}

// Ícono + color por tipo de decisión (aún sin elegir = ayuda visual neutra).
const TIPO_ICONO: Record<TipoDecisionInicial, LucideIcon> = {
  AUTO_INHIBITORIO: Ban,
  INDAGACION_PREVIA: Search,
  INVESTIGACION: FolderSearch,
};
const TIPO_TONO: Record<TipoDecisionInicial, string> = {
  AUTO_INHIBITORIO: 'bg-muted text-muted-foreground',
  INDAGACION_PREVIA: 'bg-blue-100 text-blue-700',
  INVESTIGACION: 'bg-violet-100 text-violet-700',
};
function iconoTipo(tipo?: TipoDecisionInicial | null): LucideIcon {
  return tipo ? TIPO_ICONO[tipo] : HelpCircle;
}
function tonoTipo(tipo?: TipoDecisionInicial | null): string {
  return tipo ? TIPO_TONO[tipo] : 'bg-muted text-muted-foreground';
}

// Color del badge de estado: pendiente = atención, devuelta = requiere corrección, aprobada = en firme.
function tonoEstado(e: EstadoDecisionInicial): string {
  if (e === 'APROBADA') return 'bg-primary/10 text-primary';
  if (e.startsWith('DEVUELTA')) return 'bg-destructive/10 text-destructive';
  return 'bg-amber-100 text-amber-700';
}

const TODOS = 'TODOS';

export default function BandejaDecisiones({ onAbrir }: { onAbrir?: (id: string) => void }) {
  const [filas, setFilas] = useState<DecisionBandeja[]>([]);
  const [cargando, setCargando] = useState(true);
  // Motivo de la devolución a mostrar en el diálogo ({radicado, motivo}).
  const [motivoVer, setMotivoVer] = useState<{ radicado: string; motivo: string } | null>(null);
  const rol = usuarioActual()?.rol ?? '';

  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState(TODOS);

  async function cargar() {
    setCargando(true);
    try {
      setFilas(await bandejaDecisiones());
    } catch (e) {
      toast.error('No se pudo cargar la bandeja de decisiones', {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => { cargar(); }, []);

  const conteo = useMemo(
    () => ({
      total: filas.length,
      pendientes: filas.filter((d) => !d.estado.startsWith('DEVUELTA') && d.estado !== 'APROBADA').length,
      devueltas: filas.filter((d) => d.estado.startsWith('DEVUELTA')).length,
    }),
    [filas],
  );

  // Estados presentes en la bandeja cargada (para no ofrecer opciones vacías en el filtro).
  const estadosPresentes = useMemo(
    () => Array.from(new Set(filas.map((d) => d.estado))),
    [filas],
  );

  const hayFiltros = busqueda.trim() !== '' || filtroEstado !== TODOS;
  function limpiarFiltros() {
    setBusqueda('');
    setFiltroEstado(TODOS);
  }

  const filasFiltradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return filas.filter((d) => {
      if (filtroEstado !== TODOS && d.estado !== filtroEstado) return false;
      if (!q) return true;
      const texto = `${d.radicado} ${d.radicadoManual ?? ''} ${d.investigado ?? ''}`.toLowerCase();
      return texto.includes(q);
    });
  }, [filas, busqueda, filtroEstado]);

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex size-10 items-center justify-center rounded-lg bg-accent text-accent-foreground">
          <Gavel className="size-5" />
        </span>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {rol === 'TECNICO_ADMINISTRATIVO' ? 'Casos devueltos' : 'Decisión inicial'}
          </h1>
          <p className="text-sm text-muted-foreground">{ayudaPorRol(rol)}</p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="En su bandeja" value={conteo.total} icon={Gavel} tone="slate" loading={cargando} />
        <StatCard label="Pendientes" value={conteo.pendientes} icon={Clock} tone="amber" loading={cargando} />
        <StatCard label="Devueltas a corregir" value={conteo.devueltas} icon={MessageSquareWarning} tone="red" loading={cargando} />
      </div>

      {/* Barra de filtros: agrupada visualmente como una sola pieza compacta. */}
      <div className="flex flex-wrap items-center gap-1.5 rounded-lg border bg-muted/30 p-1.5">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar radicado o investigado…"
            className="h-8 border-transparent bg-transparent pl-8 shadow-none focus-visible:border-input focus-visible:bg-background"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        <Select value={filtroEstado} onValueChange={setFiltroEstado}>
          <SelectTrigger size="sm" className="w-[190px] border-transparent bg-transparent shadow-none data-[state=open]:bg-background">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODOS}>Todos los estados</SelectItem>
            {estadosPresentes.map((e) => (
              <SelectItem key={e} value={e}>{labelEstadoCorto(e)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hayFiltros && (
          <Button type="button" variant="ghost" size="sm" className="h-8" onClick={limpiarFiltros}>
            <X className="size-3.5" /> Limpiar
          </Button>
        )}
        {hayFiltros && (
          <span className="ml-auto pr-1 text-xs text-muted-foreground">
            {filasFiltradas.length} de {filas.length}
          </span>
        )}
      </div>

      <Card className="py-2">
        <CardContent className="px-2">
          {cargando ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Cargando…</p>
          ) : filas.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-14 text-center">
              <span className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Gavel className="size-5" />
              </span>
              <p className="text-sm font-medium text-foreground">Su bandeja está al día</p>
              <p className="text-xs text-muted-foreground">
                No hay casos pendientes en {rol === 'TECNICO_ADMINISTRATIVO' ? 'casos devueltos' : 'decisión inicial'}.
              </p>
            </div>
          ) : filasFiltradas.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm text-muted-foreground">
                No se encontraron casos con esos filtros.
              </p>
              <Button type="button" variant="link" size="sm" onClick={limpiarFiltros}>
                Limpiar filtros
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-border/70">
              {filasFiltradas.map((d) => {
                const TipoIcon = iconoTipo(d.tipo);
                const puedeVerMotivo = d.estado.startsWith('DEVUELTA') && (d.motivoDevolucion || d.comentariosDirector);
                const fecha = d.fechaRevision ?? d.fechaPropuesta;

                return (
                  <div
                    key={d.id}
                    className="group flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg px-2 py-3 transition-colors hover:bg-muted/40 sm:flex-nowrap"
                  >
                    <span className={cn('flex size-11 shrink-0 items-center justify-center rounded-full', tonoTipo(d.tipo))}>
                      <TipoIcon className="size-5" />
                    </span>

                    <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-foreground">
                          {d.radicadoManual || d.radicado}
                        </span>
                        <Badge variant="outline" className={cn('shrink-0 border-transparent', tonoEstado(d.estado))}>
                          {labelEstadoCorto(d.estado)}
                        </Badge>
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                        <span className="font-medium text-foreground/70">{labelTipoDecision(d.tipo)}</span>
                        <span className="text-muted-foreground/40">·</span>
                        <span className="truncate">{d.investigado ?? 'Investigado por determinar'}</span>
                        <span className="text-muted-foreground/40">·</span>
                        <span className="inline-flex shrink-0 items-center gap-1">
                          <History className="size-3" />
                          {fecha ? `Actualizado ${fmtFecha(fecha)}` : 'Sin movimientos aún'}
                        </span>
                      </div>
                      {/* Estado completo del trámite, solo visible con hover/foco (detalle sin saturar la fila). */}
                      <p className="mt-0.5 text-xs text-muted-foreground/0 transition-colors group-hover:text-muted-foreground">
                        {labelEstadoDecision(d.estado)}
                      </p>
                    </div>

                    {/* Acciones frecuentes: siempre visibles, no escondidas en un menú. */}
                    <div className="ml-auto flex shrink-0 items-center gap-1.5">
                      {puedeVerMotivo && (
                        <Button
                          size="icon-sm" variant="ghost"
                          onClick={() => setMotivoVer({
                            radicado: d.radicadoManual || d.radicado,
                            motivo: d.motivoDevolucion || d.comentariosDirector || '',
                          })}
                          aria-label="Ver motivo de la devolución" title="Ver motivo de la devolución"
                        >
                          <MessageSquareWarning className="size-4" />
                        </Button>
                      )}
                      <Button size="sm" onClick={() => onAbrir?.(d.expedienteId)}>
                        <Eye className="size-3.5" /> Atender
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!motivoVer} onOpenChange={(o) => { if (!o) setMotivoVer(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquareWarning className="size-5 text-primary" /> Motivo de la devolución
            </DialogTitle>
            {motivoVer && (
              <DialogDescription>Radicado <strong>{motivoVer.radicado}</strong></DialogDescription>
            )}
          </DialogHeader>
          {motivoVer && (
            <p className="whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm">{motivoVer.motivo}</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
